// notificationsApi — counselor_student_notifications CRUD + realtime | student receives feedback alerts | reads from counselor_student_notifications table

import { supabase } from '../lib/supabase'

export interface StudentNotification {
  id: string
  student_id: string
  counselor_id: string
  notification_type: string   // 'essay_feedback' | 'essay_inline_comment' | 'activity_feedback'
  entity_type: string         // 'essay' | 'supplement' | 'activity' | 'activities'
  entity_id: string
  entity_name: string | null
  related_entity_id: string | null
  message: string | null
  is_read: boolean
  read_at: string | null
  created_at: string
}

/** Fetch all notifications for a student, newest first */
export async function getStudentNotifications(studentId: string): Promise<StudentNotification[]> {
  const { data, error } = await supabase
    .from('counselor_student_notifications')
    .select('*')
    .eq('student_id', studentId)
    .order('created_at', { ascending: false })
    .limit(50)

  if (error) throw error
  return data ?? []
}

/** Mark a single notification as read */
export async function markNotificationRead(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('counselor_student_notifications')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('id', notificationId)

  if (error) throw error
}

/** Mark all unread notifications for a student as read */
export async function markAllNotificationsRead(studentId: string): Promise<void> {
  const { error } = await supabase
    .from('counselor_student_notifications')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('student_id', studentId)
    .eq('is_read', false)

  if (error) throw error
}

// ==========================================
// COUNSELOR NOTIFICATIONS
// ==========================================

/** Fetch all notifications for a counselor, newest first */
export async function getCounselorNotifications(counselorId: string): Promise<StudentNotification[]> {
  const { data, error } = await supabase
    .from('counselor_student_notifications')
    .select('*')
    .eq('counselor_id', counselorId)
    .in('notification_type', ['draft_comparison', 'essay_feedback_request'])
    .order('created_at', { ascending: false })
    .limit(50)

  if (error) throw error
  return data ?? []
}

/** Mark a single counselor notification as read */
export async function markCounselorNotificationRead(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('counselor_student_notifications')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('id', notificationId)

  if (error) throw error
}

/** Mark all unread counselor notifications as read */
export async function markAllCounselorNotificationsRead(counselorId: string): Promise<void> {
  const { error } = await supabase
    .from('counselor_student_notifications')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('counselor_id', counselorId)
    .in('notification_type', ['draft_comparison', 'essay_feedback_request'])
    .eq('is_read', false)

  if (error) throw error
}

/** Subscribe to new notifications for a counselor in real-time */
export function subscribeToCounselorNotifications(
  counselorId: string,
  onNew: (notification: StudentNotification) => void,
) {
  const channel = supabase
    .channel(`counselor-notifications:${counselorId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'counselor_student_notifications',
        filter: `counselor_id=eq.${counselorId}`,
      },
      (payload) => {
        const n = payload.new as StudentNotification
        if (['draft_comparison', 'essay_feedback_request'].includes(n.notification_type)) {
          onNew(n)
        }
      },
    )
    .subscribe()

  return () => { supabase.removeChannel(channel) }
}

/** Subscribe to new notifications for a student in real-time */
export function subscribeToNotifications(
  studentId: string,
  onNew: (notification: StudentNotification) => void,
) {
  const channel = supabase
    .channel(`student-notifications:${studentId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'counselor_student_notifications',
        filter: `student_id=eq.${studentId}`,
      },
      (payload) => onNew(payload.new as StudentNotification),
    )
    .subscribe()

  return () => { supabase.removeChannel(channel) }
}
